import createLogger from '@/lib/logger'
import { useAppStore } from '@/store/app'

import router from '..'
import {
  getPostAuthRedirect,
  isPostAuthLandingRoute,
} from '../postAuthRedirect'

import type { RouteLocationNormalized } from 'vue-router'

const logger = createLogger('noAccessGuard')

function routeAllowsWithoutEverGptAccess(to: RouteLocationNormalized): boolean {
  return to.matched.some((r) => r.meta.allowWithoutEverGptAccess === true)
}

/**
 * Deep links to pages that don't need EverGPT access (a public GPT, a published
 * website, a shared HTML file) survive the Keycloak round trip in
 * `POST_AUTH_REDIRECT_URL`: login always returns to the app root, and
 * `lastVisitedRouteGuard` — the LAST guard — is what puts the user back on the link
 * they opened. A user without EverGPT access never got that far: `/users/me` 403s,
 * this guard runs first and parked them on /not-authorised, so the shared link was
 * unusable for exactly the audience it exists for.
 *
 * So consume the pending redirect here, but only when it points at a page this user
 * is allowed to see — any other saved route still ends on /not-authorised.
 *
 * Restricted to the post-auth landing page for the same reason `lastVisitedRouteGuard`
 * is: a saved link may replace the page Keycloak drops the user on, never a URL they
 * navigated to deliberately.
 */
const takePendingRedirectWithoutEverGptAccess = (
  to: RouteLocationNormalized,
) => {
  if (!isPostAuthLandingRoute(to)) return

  const pendingRedirect = getPostAuthRedirect()
  if (!pendingRedirect) return

  const target = router.resolve(pendingRedirect)
  if (!target.matched.some((r) => r.meta.allowWithoutEverGptAccess === true)) {
    return
  }

  // Consume it so `lastVisitedRouteGuard` doesn't re-fire the same navigation.
  getPostAuthRedirect(true)
  return { path: target.path, query: target.query, hash: target.hash }
}

/**
 * Handles users without access to the app
 */
export const noAccessGuard = async (to: RouteLocationNormalized) => {
  logger.log('Running hook...')

  // Allow users to log out if they don't have access
  if (to.name === 'Logout') {
    return true
  }

  if (
    useAppStore().isUserAuthorisedForEverGpt ||
    to.meta.isPublic ||
    to.meta.allowAnonymous ||
    routeAllowsWithoutEverGptAccess(to)
  ) {
    logger.log('User has access to EverGPT or is a platform admin')
    // Redirect away from access page
    if (to.name === 'NotAuthorised') {
      logger.log('Already on NotAuthorised, redirecting to new chat')
      return { name: 'NewChat' }
    }
    return
  } else {
    logger.log('User does not have access to the app.')

    const pendingRedirect = takePendingRedirectWithoutEverGptAccess(to)
    if (pendingRedirect) {
      logger.log('Restoring shared link opened before login:', pendingRedirect)
      return pendingRedirect
    }

    // To avoid infinite loops
    return to.name === 'NotAuthorised' ? true : { name: 'NotAuthorised' }
  }
}
